import {ThreeTsl as TSL} from "#material/three/exports"
import type {Three as THREE} from "#material/three/exports"
import {threeValue} from "#material/three/three-utils"

// Cycles is what the render answers with, so these follow its own definitions rather than the usual ones:
// smoothminf, wrapf, pingpongf and safe_divide in util/math.h of the kernel the render server builds.
type Float = THREE.Node<"float">

const FLOAT_EPSILON = 1.1920929e-7

export const safeDivide = (a: Float, b: Float): Float => TSL.select(b.equal(threeValue(0)), threeValue(0), TSL.div(a, b))

export const smoothMinimum = (a: Float, b: Float, distance: Float): Float => {
    const blend = TSL.div(TSL.max(TSL.sub(distance, TSL.abs(TSL.sub(a, b))), threeValue(0)), distance)
    const smooth = TSL.sub(TSL.min(a, b), TSL.mul(TSL.mul(TSL.mul(blend, TSL.mul(blend, blend)), distance), threeValue(1 / 6)))
    return TSL.select(distance.equal(threeValue(0)), TSL.min(a, b), smooth)
}

export const wrapped = (value: Float, maximum: Float, minimum: Float): Float => {
    const span = TSL.sub(maximum, minimum)
    const folded = TSL.sub(value, TSL.mul(span, TSL.floor(TSL.div(TSL.sub(value, minimum), span))))
    return TSL.select(span.equal(threeValue(0)), minimum, folded)
}

export const pingPong = (value: Float, scale: Float): Float => {
    const span = TSL.mul(scale, threeValue(2))
    const bounced = TSL.abs(TSL.sub(TSL.mul(TSL.fract(TSL.div(TSL.sub(value, scale), span)), span), scale))
    return TSL.select(scale.equal(threeValue(0)), threeValue(0), bounced)
}

export const withinDistance = (a: Float, b: Float, distance: Float): Float =>
    TSL.select(TSL.abs(TSL.sub(a, b)).lessThanEqual(TSL.max(distance, threeValue(FLOAT_EPSILON))), threeValue(1), threeValue(0))

export const flooredModulo = (a: Float, b: Float): Float => TSL.select(b.equal(threeValue(0)), threeValue(0), TSL.sub(a, TSL.mul(TSL.floor(TSL.div(a, b)), b)))

import type {ScalarMathNodeOperation} from "#material/nodes/converter/scalar-math/constants"
import type {MathNode} from "#material/cycles/types"

export const convertOperationToMathType = (operation?: ScalarMathNodeOperation): MathNode["parameters"]["math_type"] => {
    if (!operation) {
        return undefined
    }

    switch (operation) {
        case "ABSOLUTE":
            return "absolute"
        case "ADD":
            return "add"
        case "ARCCOSINE":
            return "arccosine"
        case "ARCSINE":
            return "arcsine"
        case "ARCTANGENT":
            return "arctangent"
        case "ARCTAN2":
            return "arctan2"
        case "CEIL":
            return "ceil"
        case "COMPARE":
            return "compare"
        case "COSH":
            return "cosh"
        case "COSINE":
            return "cosine"
        case "DEGREES":
            return "degrees"
        case "DIVIDE":
            return "divide"
        case "EXPONENT":
            return "exponent"
        case "FLOOR":
            return "floor"
        case "FLOORED_MODULO":
            return "floored_modulo"
        case "GREATER_THAN":
            return "greater_than"
        case "INVERSE_SQRT":
            return "inversesqrt"
        case "LOGARITHM":
            return "logarithm"
        case "MAXIMUM":
            return "maximum"
        case "MINIMUM":
            return "minimum"
        case "MODULO":
            return "modulo"
        case "MULTIPLY":
            return "multiply"
        case "MULTIPLY_ADD":
            return "multiply_add"
        case "LESS_THAN":
            return "less_than"
        case "RADIANS":
            return "radians"
        case "PINGPONG":
            return "pingpong"
        case "POWER":
            return "power"
        case "ROUND":
            return "round"
        case "SIGN":
            return "sign"
        case "SINE":
            return "sine"
        case "SINH":
            return "sinh"
        case "SMOOTH_MAX":
            return "smoothmax"
        case "SNAP":
            return "snap"
        case "SMOOTH_MIN":
            return "smoothmin"
        case "SQRT":
            return "sqrt"
        case "SUBTRACT":
            return "subtract"
        case "TANGENT":
            return "tangent"
        case "TANH":
            return "tanh"
        case "TRUNC":
            return "trunc"
        case "WRAP":
            return "wrap"
        case "FRACT":
            throw new Error("FRACT operation is not supported")
    }
}

export const numberOfInputsUsed = (operation?: ScalarMathNodeOperation): number => {
    if (!operation) {
        return 0
    }
    switch (operation) {
        case "ABSOLUTE":
        case "CEIL":
        case "FLOOR":
        case "FRACT":
        case "SIGN":
        case "ROUND":
        case "TRUNC":
        case "EXPONENT":
        case "ARCCOSINE":
        case "ARCSINE":
        case "ARCTANGENT":
        case "COSH":
        case "COSINE":
        case "DEGREES":
        case "INVERSE_SQRT":
        case "RADIANS":
        case "SINE":
        case "SINH":
        case "SQRT":
        case "TANGENT":
        case "TANH":
            return 1

        case "ADD":
        case "SUBTRACT":
        case "MULTIPLY":
        case "DIVIDE":
        case "MODULO":
        case "POWER":
        case "LOGARITHM":
        case "ARCTAN2":
        case "GREATER_THAN":
        case "LESS_THAN":
        case "FLOORED_MODULO":
        case "MAXIMUM":
        case "MINIMUM":
        case "PINGPONG":
        case "SMOOTH_MAX":
        case "SNAP":
        case "SMOOTH_MIN":
            return 2

        case "COMPARE":
        case "MULTIPLY_ADD":
        case "WRAP":
            return 3
    }
}
